import type { Payload as BoomError } from '@hapi/boom';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

/**
 * Error object format returned by python backend
 * trigger by error like
 *   return {"error": {"code": 401, "message": errorMessage}}, 401
 */
interface PyError {
  error: {
    code: number;
    message: string;
  };
  status: number;
}

/**
 * Common error returned by errorHandler
 */
type ErrorHandlerResult = {
  statusCode: number | string;
  message: string;
};

/**
 * Error Handler for getting:
 * - status code
 * - and error message
 *
 * out from HTTP calls
 */
export function errorHandler(err: any): ErrorHandlerResult {
  // Default error message content
  let statusCode: string | number = 500;
  let message = 'Internal Error';

  if (typeof err.status === 'number') {
    statusCode = err.status;
    if ('data' in err) {
      const { data } = err;
      if ('statusCode' in data && ('message' in data || 'error' in data)) {
        // Looks like an error object produced by @hapi/boom
        const errObj: BoomError = data;
        statusCode = errObj.statusCode;
        message = errObj.message || errObj.error;
      } else if ('error' in data && typeof data.error === 'object') {
        // Probably coming from python backend
        const errObj: PyError = data;
        statusCode = errObj.status || errObj.error.code;
        message = errObj.error.message;
      }
    }
  } else if (typeof err.status === 'string') {
    // Could be generic fetch error
    const errObj: FetchBaseQueryError = err;
    if (errObj.status === 'PARSING_ERROR') {
      /**
       *   data: ...
       *   error: '...'
       *   originalStatus: 400
       *   status: 'PARSING_ERROR' | 'FETCH_ERROR' ...
       */
      statusCode = errObj.originalStatus;
      message = errObj.error;
    } else if ('error' in errObj) {
      statusCode = errObj.status;
      message = errObj.error;
    }
  }

  return { statusCode, message };
}
